import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Rocket } from "lucide-react";

const MAX_POLLS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }

    const poll = async () => {
      if (polls.current >= MAX_POLLS) { setStatus("failed"); return; }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <div className="p-6"><Logo /></div>
      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full rounded-2xl border border-border/50 bg-card p-10 text-center" data-testid="payment-success-card">
          {status === "checking" && (
            <>
              <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
              <h1 className="font-heading text-2xl font-bold mt-6">Confirming your payment…</h1>
              <p className="text-muted-foreground mt-2">Hang tight, this only takes a moment.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="w-16 h-16 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-primary" /></div>
              <h1 className="font-heading text-3xl font-black mt-6">You're all set! 🚀</h1>
              <p className="text-muted-foreground mt-2">Your campaign is now live. Our AI is targeting real viewers as you read this.</p>
              <Button onClick={() => navigate("/dashboard")} className="mt-7 rounded-full h-11 px-8 bg-primary text-background font-bold hover:bg-primary/90 transition-colors" data-testid="go-dashboard-btn">
                View my dashboard <Rocket className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {status === "failed" && (
            <>
              <div className="w-16 h-16 rounded-2xl bg-secondary/15 flex items-center justify-center mx-auto"><XCircle className="w-9 h-9 text-secondary" /></div>
              <h1 className="font-heading text-2xl font-bold mt-6">Couldn't confirm payment</h1>
              <p className="text-muted-foreground mt-2">If you were charged, it may take a minute to reflect. Please check your dashboard or try again.</p>
              <div className="flex gap-3 justify-center mt-7">
                <Button variant="outline" onClick={() => navigate("/plans")} className="rounded-full border-white/15">Back to plans</Button>
                <Button onClick={() => navigate("/dashboard")} className="rounded-full bg-primary text-background font-bold">Go to dashboard</Button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
